import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';

/**
 * Globo Product Options injects .gpo-canvas-container over the gallery after load.
 * That canvas intercepts clicks and breaks zoom. Allow clicks to pass through.
 *
 * @param {ParentNode} [root=document]
 * @returns {number} Number of GPO overlays fixed
 */
function unblockZoomFromGpo(root = document) {
  const containers = root.querySelectorAll?.('.gpo-canvas-container') ?? [];
  let fixed = 0;

  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    container.style.setProperty('pointer-events', 'none', 'important');

    container.querySelectorAll('canvas, .konvajs-content').forEach((node) => {
      if (node instanceof HTMLElement) {
        node.style.setProperty('pointer-events', 'none', 'important');
      }
    });

    fixed += 1;
  });

  return fixed;
}

/**
 * Watch for late-injected GPO canvas overlays and unblock zoom automatically.
 * @param {Element} gallery
 */
function observeGpoCanvas(gallery) {
  if (!(gallery instanceof Element) || gallery.dataset.gpoZoomObserver === 'true') return;

  gallery.dataset.gpoZoomObserver = 'true';
  unblockZoomFromGpo(gallery);

  const observer = new MutationObserver(() => {
    unblockZoomFromGpo(gallery);
  });

  observer.observe(gallery, { childList: true, subtree: true });
}

/**
 * Reinit product image zoom from the browser console or after DOM swaps.
 *
 * Usage: reinitProductZoom()
 *
 * @param {ParentNode | Element | null} [root]
 * @returns {{ mode: string, ok: boolean, detail?: unknown }}
 */
function reinitProductZoom(root) {
  const scope =
    root instanceof Element || root instanceof DocumentFragment || root === document
      ? root
      : document;

  /** @type {Element[]} */
  const galleries = [];

  if (scope instanceof Element && scope.matches?.('media-gallery')) {
    galleries.push(scope);
  } else if (scope.querySelectorAll) {
    galleries.push(...scope.querySelectorAll('media-gallery'));
  }

  if (!galleries.length) {
    const message = '[product-zoom] No <media-gallery> found on this page.';
    console.warn(message);
    return { mode: 'none', ok: false, detail: message };
  }

  let hoverBound = 0;
  let gpoFixed = 0;

  for (const gallery of galleries) {
    gpoFixed += unblockZoomFromGpo(gallery);
    observeGpoCanvas(gallery);

    const isHoverMode = gallery.hasAttribute('data-zoom-on-hover');

    if (isHoverMode) {
      if (typeof window.enableZoomOnHover === 'function') {
        hoverBound += Number(window.enableZoomOnHover(gallery, 2, { force: true })) || 0;
      } else {
        console.warn(
          '[product-zoom] Hover mode is on, but magnify.js is not loaded. Hard-refresh the page.'
        );
      }
    }
  }

  if (gpoFixed > 0) {
    console.info(
      `[product-zoom] Unblocked zoom under ${gpoFixed} Globo (gpo-canvas-container) overlay(s).`
    );
  }

  if (hoverBound > 0) {
    console.info(`[product-zoom] Hover zoom rebound on ${hoverBound} image(s).`);
    return { mode: 'hover', ok: true, detail: { hoverBound, gpoFixed } };
  }

  const hasDialog = galleries.some((gallery) => gallery.querySelector('zoom-dialog'));

  if (hasDialog || gpoFixed > 0) {
    console.info('[product-zoom] Dialog zoom ready. Click a product image to open zoom.');
    return { mode: 'dialog', ok: true, detail: { gpoFixed } };
  }

  return { mode: 'unknown', ok: false, detail: { gpoFixed } };
}

// Always available in DevTools (media-gallery.js loads on every page).
window.reinitProductZoom = reinitProductZoom;
window.unblockZoomFromGpo = unblockZoomFromGpo;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideChange, { signal });

    // GPO swatches live outside the gallery; listen once (delegation) so late-rendered inputs work.
    document.addEventListener('change', this.#handleGpoSwatchChange, { signal });

    // GPO canvas is injected asynchronously after options load.
    observeGpoCanvas(this);
    unblockZoomFromGpo(this);
  }

  #controller = new AbortController();

  /** When true, keep re-applying hide class if GPO recreates/updates the canvas. */
  #gpoCanvasShouldHide = false;

  /** @type {MutationObserver | null} */
  #gpoCanvasHideObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#gpoCanvasHideObserver?.disconnect();
    this.#gpoCanvasHideObserver = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    requestAnimationFrame(() => {
      reinitProductZoom(newMediaGallery);
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow slide changes (arrows, thumbnails, drag, scroll).
   * Re-applies GPO click-through so zoom keeps working after each slide.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideChange = (event) => {
    unblockZoomFromGpo(this);

    if (typeof window.enableZoomOnHover === 'function' && this.hasAttribute('data-zoom-on-hover')) {
      window.enableZoomOnHover(this, 2, { force: true });
    }

    // Hide GPO live-preview overlay while browsing gallery slides.
    // Observer keeps the class if GPO recreates/updates the canvas later.
    this.#setGpoCanvasTestClass(true);

    this.dispatchEvent(
      new CustomEvent('media-gallery:slide-change', {
        bubbles: true,
        detail: event.detail,
      })
    );
  };

  /**
   * When a Globo swatch/option input changes, show the GPO canvas again.
   * @param {Event} event
   */
  #handleGpoSwatchChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.closest('.gpo-swatches')) return;

    this.#setGpoCanvasTestClass(false);
  };

  /**
   * Watch for GPO canvas inject/replace and re-apply hide class while slide-browsing.
   */
  #ensureGpoCanvasHideObserver() {
    if (this.#gpoCanvasHideObserver) return;

    this.#gpoCanvasHideObserver = new MutationObserver(() => {
      if (!this.#gpoCanvasShouldHide) return;
      this.#applyGpoCanvasHideClass();
    });

    this.#gpoCanvasHideObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  #applyGpoCanvasHideClass() {
    this.querySelectorAll('.gpo-canvas-container').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      if (!container.classList.contains('gpo-canvas-container-hide')) {
        container.classList.add('gpo-canvas-container-hide');
      }
    });
  }

  /**
   * Toggle hide class on .gpo-canvas-container.
   * While enabled, a MutationObserver re-applies it whenever GPO updates the canvas.
   * @param {boolean} add
   */
  #setGpoCanvasTestClass(add) {
    this.#gpoCanvasShouldHide = add;
    this.#ensureGpoCanvasHideObserver();

    if (add) {
      this.#applyGpoCanvasHideClass();
      return;
    }

    this.querySelectorAll('.gpo-canvas-container').forEach((container) => {
      if (container instanceof HTMLElement) {
        container.classList.remove('gpo-canvas-container-hide');
      }
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
